import React, { useState, useRef, useEffect } from 'react';
import { getOptimizedProductImage, isCloudinaryUrl, optimizeCloudinaryUrl } from '../utils/cloudinaryUtils';
import './OptimizedImage.css';

/**
 * OptimizedImage Component
 * 
 * Features:
 * - Responsive images with srcset
 * - Lazy loading with Intersection Observer
 * - Blur-up placeholder effect
 * - WebP format with fallbacks
 * - Error handling with fallback image
 * 
 * @param {string} src - Original image URL
 * @param {string} alt - Alt text for accessibility
 * @param {string} className - Additional CSS classes
 * @param {string} sizes - Sizes attribute for responsive images (default: responsive)
 * @param {boolean} priority - If true, loads immediately (no lazy loading)
 * @param {string} aspectRatio - Aspect ratio for placeholder (e.g., "16/9", "1/1")
 * @param {object} style - Additional inline styles
 * @param {function} onLoad - Callback when image loads
 * @param {function} onError - Callback when image fails to load
 */
const OptimizedImage = ({
  src,
  alt = '',
  className = '',
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
  priority = false,
  aspectRatio = null,
  style = {},
  onLoad,
  onError,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isInView, setIsInView] = useState(priority);
  const [hasError, setHasError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // Generate optimized URLs for different sizes
  const generateImageUrls = (imageUrl) => {
    if (!imageUrl) {
      return null;
    }
    
    if (!isCloudinaryUrl(imageUrl)) {
      // For non-Cloudinary URLs, return as-is
      return {
        thumbnail: imageUrl,
        small: imageUrl,
        medium: imageUrl,
        large: imageUrl,
        webpSmall: imageUrl,
        webpMedium: imageUrl,
        webpLarge: imageUrl
      };
    }

    return {
      // Thumbnail (20px) - for blur placeholder (very small for fast load)
      thumbnail: optimizeCloudinaryUrl(imageUrl, {
        width: 20,
        height: 20,
        crop: 'fill',
        quality: 'auto:low',
        format: 'auto'
      }),
      // Small (300px)
      small: optimizeCloudinaryUrl(imageUrl, {
        width: 300,
        crop: 'limit',
        quality: 'auto:good',
        format: 'auto'
      }),
      // Medium (600px)
      medium: optimizeCloudinaryUrl(imageUrl, {
        width: 600,
        crop: 'limit',
        quality: 'auto:good',
        format: 'auto'
      }),
      // Large (1200px)
      large: optimizeCloudinaryUrl(imageUrl, {
        width: 1200,
        crop: 'limit',
        quality: 'auto:best',
        format: 'auto'
      }),
      // WebP versions for different sizes
      webpSmall: optimizeCloudinaryUrl(imageUrl, {
        width: 300,
        crop: 'limit',
        quality: 'auto:good',
        format: 'webp'
      }),
      webpMedium: optimizeCloudinaryUrl(imageUrl, {
        width: 600,
        crop: 'limit',
        quality: 'auto:good',
        format: 'webp'
      }),
      webpLarge: optimizeCloudinaryUrl(imageUrl, {
        width: 1200,
        crop: 'limit',
        quality: 'auto:best',
        format: 'webp'
      })
    };
  };

  const imageUrls = src ? generateImageUrls(src) : null;

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            // Once in view, we can stop observing
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.01
      }
    );

    observer.observe(containerRef.current);

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [priority]);

  // Handle image load
  const handleLoad = (e) => {
    setIsLoading(false);
    setImageLoaded(true);
    if (onLoad) onLoad(e);
  };

  // Handle image error
  const handleError = (e) => {
    setHasError(true);
    setIsLoading(false);
    if (onError) onError(e);
  };

  // Fallback image
  const fallbackImage = 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80';

  if (!src || hasError) {
    return (
      <div
        className={`optimized-image-container optimized-image-error ${className}`}
        style={{ aspectRatio: aspectRatio || '16/9', ...style }}
        ref={containerRef}
      >
        <img
          src={fallbackImage}
          alt={alt || 'Content unavailable'}
          className="optimized-image"
          loading="eager"
          {...props}
        />
      </div>
    );
  }

  // If src is empty string, show fallback immediately
  if (src === '') {
    return (
      <div
        className={`optimized-image-container optimized-image-error ${className}`}
        style={{ aspectRatio: aspectRatio || '16/9', ...style }}
        ref={containerRef}
      >
        <img
          src={fallbackImage}
          alt={alt || 'Content unavailable'}
          className="optimized-image"
          loading="eager"
          {...props}
        />
      </div>
    );
  }

  return (
    <div
      className={`optimized-image-container ${className}`}
      style={{ aspectRatio: aspectRatio || 'auto', ...style }}
      ref={containerRef}
    >
      {/* Blur-up placeholder */}
      {isLoading && imageUrls && (
        <div
          className="optimized-image-placeholder"
          style={{
            backgroundImage: `url(${imageUrls.thumbnail})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(20px)',
            transform: 'scale(1.1)'
          }}
        />
      )}

      {/* Main image */}
      {isInView && imageUrls && (
        <picture>
          {/* WebP source for modern browsers */}
          {imageUrls.webpSmall && imageUrls.webpMedium && imageUrls.webpLarge && (
            <source
              srcSet={`
                ${imageUrls.webpSmall} 300w,
                ${imageUrls.webpMedium} 600w,
                ${imageUrls.webpLarge} 1200w
              `}
              sizes={sizes}
              type="image/webp"
            />
          )}
          {/* Fallback for browsers that don't support WebP */}
          {imageUrls.small && imageUrls.medium && imageUrls.large && (
            <source
              srcSet={`
                ${imageUrls.small} 300w,
                ${imageUrls.medium} 600w,
                ${imageUrls.large} 1200w
              `}
              sizes={sizes}
            />
          )}
          <img
            ref={imgRef}
            src={imageUrls.medium || imageUrls.small || src}
            srcSet={imageUrls.small && imageUrls.medium && imageUrls.large ? `
              ${imageUrls.small} 300w,
              ${imageUrls.medium} 600w,
              ${imageUrls.large} 1200w
            ` : undefined}
            sizes={sizes}
            alt={alt}
            className={`optimized-image ${imageLoaded ? 'optimized-image-loaded' : ''}`}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            onLoad={handleLoad}
            onError={handleError}
            {...props}
          />
        </picture>
      )}
      
      {/* Fallback if imageUrls couldn't be generated */}
      {isInView && !imageUrls && src && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className={`optimized-image ${imageLoaded ? 'optimized-image-loaded' : ''}`}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          {...props}
        />
      )}

      {/* Loading skeleton (if not using blur placeholder) */}
      {isLoading && !imageUrls && (
        <div className="optimized-image-skeleton" />
      )}
    </div>
  );
};

export default OptimizedImage;

